import { queryClient } from '@/lib/query-client'
import { useReloginDialogStore } from '@/stores/relogin-dialog'
import { isPostHogEnabled, posthog } from '@/lib/posthog'
import { SAVED_FILTERS_KEY_PREFIX } from '@/shared/painel-saved-filters'

/**
 * Remove cookies que o JavaScript consegue ler (não remove HttpOnly).
 */
function clearAccessibleCookies(): void {
  if (typeof document === 'undefined') return

  const hostname = window.location.hostname
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'

  const tryClear = (name: string, path: string, domain?: string) => {
    let cookie = `${encodeURIComponent(name)}=;${expire};path=${path}`
    if (domain) cookie += `;domain=${domain}`
    document.cookie = cookie
  }

  const names = document.cookie.split(';').flatMap((part) => {
    const eq = part.indexOf('=')
    const rawName = (eq > -1 ? part.slice(0, eq) : part).trim()
    if (!rawName) return []
    try {
      return [decodeURIComponent(rawName)]
    } catch {
      return [rawName]
    }
  })

  for (const name of names) {
    tryClear(name, '/')

    if (hostname && hostname !== 'localhost') {
      tryClear(name, '/', hostname)
      const segments = hostname.split('.')
      if (segments.length >= 2) {
        tryClear(name, '/', `.${segments.slice(-2).join('.')}`)
      }
    }
  }
}

/**
 * Limpa dados da sessão anterior no cliente: React Query, storages, cookies
 * acessíveis e identificação em analytics (quando habilitado).
 * Usar apenas no **logout explícito** (`useAuthStore.getState().logout()`), não no fluxo de
 * relogin com senha no modal — nesse caso basta `clearAuth()` para manter filtros e cache
 * enquanto o usuário permanece na mesma sessão de navegação após reautenticar.
 */
export function clearClientSession(): void {
  if (isPostHogEnabled) {
    posthog.reset()
  }

  queryClient.clear()
  useReloginDialogStore.getState().close()

  try {
    sessionStorage.clear()
  } catch {
    // modo privado / storage bloqueado
  }
  try {
    // Filtros salvos do Painel são por usuário e sobrevivem ao logout.
    const savedFilters = Object.keys(localStorage)
      .filter((key) => key.startsWith(SAVED_FILTERS_KEY_PREFIX))
      .map((key) => [key, localStorage.getItem(key)] as const)
    localStorage.clear()
    for (const [key, value] of savedFilters) {
      if (value !== null) localStorage.setItem(key, value)
    }
  } catch {
    // modo privado / storage bloqueado
  }

  clearAccessibleCookies()
}
